import { AlertCircle } from "lucide-react";
import { CreateUserRoleContainerProps } from "pages/settings/create";
import { FC } from "react";
import { FormikProps } from "formik";
import { TextInput } from "components/core";
import _ from "lodash";
import { applicationFeatures } from "_graphql/data/features";
import { filterListByCurrentDomain } from "utils";

export interface CreateUserRoleFormProps {
  form: FormikProps<CreateUserRoleContainerProps>;
}

const CreateUserRoleForm: FC<CreateUserRoleFormProps> = ({ form }) => {
  const features = filterListByCurrentDomain(applicationFeatures);

  const allValues = _.flatMap(features, (s) =>
    _.map(s.permissions, (p) => p.value)
  );

  const handlePrivilegeChange = (permissionValue: string, checked: boolean) => {
    let privileges: string[] = form.values.privileges || [];

    // handling Global wildcard
    if (permissionValue === "*:*") {
      privileges = checked ? _.uniq([...privileges, ...allValues]) : [];
      form.setFieldValue("privileges", privileges);
      return;
    }

    // Section wildcard (e.g. "offers:*")
    if (permissionValue.endsWith(":*")) {
      const prefix = permissionValue.split(":")[0] + ":";
      const sectionValues = _.filter(allValues, (v) => v.startsWith(prefix));
      if (checked) {
        privileges = _.uniq([...privileges, ...sectionValues]);
      } else {
        privileges = privileges.filter((v) => !v.startsWith(prefix));
      }
      // After toggling section, ensure global wildcard is updated below
      form.setFieldValue("privileges", privileges);
      // continue to sync wildcards
    } else {
      // Individual permission
      if (checked) {
        privileges = _.uniq([...privileges, permissionValue]);
      } else {
        privileges = privileges.filter((v) => v !== permissionValue);
      }
      form.setFieldValue("privileges", privileges);
    }

    // Sync section wildcards: if all specific permissions in a section are selected, include that section's :* value (if present); otherwise remove it.
    features.forEach((section) => {
      const sectionAll = _.map(section.permissions, (p) => p.value);
      const sectionSpecific = sectionAll.filter(
        (v) => v !== "*:*" && !v.endsWith(":*")
      );
      const sectionWildcard = sectionAll.find((v) => v.endsWith(":*"));
      const allSelected =
        _.difference(sectionSpecific, privileges).length === 0;
      if (sectionWildcard) {
        if (allSelected) {
          if (!privileges.includes(sectionWildcard)) {
            privileges.push(sectionWildcard);
          }
        } else {
          privileges = privileges.filter((v) => v !== sectionWildcard);
        }
      }
    });

    // Sync global wildcard if all specific permissions across all sections are selected
    const allSpecific = allValues.filter(
      (v) => v !== "*:*" && !v.endsWith(":*")
    );
    const allSelected = _.difference(allSpecific, privileges).length === 0;
    if (allSelected) {
      if (allValues.includes("*:*") && !privileges.includes("*:*")) {
        privileges.push("*:*");
      }
    } else {
      privileges = privileges.filter((v) => v !== "*:*");
    }

    privileges = _.uniq(privileges);
    form.setFieldValue("privileges", privileges);
  };

  return (
    <div className="space-y-6 divide-y divide-gray-200">
      <div>
        <TextInput
          id="position"
          label="Position"
          type="text"
          placeholder="e.g. CEO"
          required={true}
          {...form}
        />
      </div>
      <div className="space-y-6 pt-5">
        <div className="flex items-center justify-between">
          <div className="block text-sm font-medium text-primary">
            Privileges <span className="text-red-500">*</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            <span>Critical privileges require admin approval</span>
          </div>
        </div>

        <div className="space-y-6 mt-2">
          {/* Masonry using CSS columns */}
          <div className="border border-gray-300 bg-gray-50 p-6 rounded columns-1 sm:columns-2 md:columns-3 lg:columns-2 gap-x-6">
            {_.map(features, (section) => (
              <div key={section.name} className="break-inside-avoid mb-6">
                <span className="block text-base font-bold underline text-gray-700">
                  {_.startCase(section.name)}
                </span>
                <div className="mt-1 w-full space-y-1">
                  {_.map(section.permissions, (permission) => (
                    <div
                      key={permission.value}
                      className="relative flex items-start"
                    >
                      <div className="flex h-6 items-center">
                        <input
                          id={`${permission?.value}.permissions`}
                          aria-describedby={`${permission.value}-description`}
                          name={`${permission?.value}.permissions`}
                          type="checkbox"
                          value={permission.value}
                          checked={_.includes(
                            form?.values?.privileges,
                            permission.value
                          )}
                          onChange={(e) =>
                            handlePrivilegeChange(
                              permission.value,
                              e.target.checked
                            )
                          }
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                        />
                      </div>
                      <div className="ml-3 text-sm leading-6">
                        <label
                          htmlFor={`${permission?.value}.permissions`}
                          className="font-medium text-gray-900"
                        >
                          {permission.name}
                        </label>
                        {true && (
                          <p
                            id="candidates-description"
                            className="text-gray-500"
                          >
                            {permission.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateUserRoleForm;
